// ============================================================================
// chatTranscript — расшифровка голосового сообщения (без React).
//
// Модель хранения (решение по коду, proto/миграции НЕ менялись):
//   • аудио — вложение сообщения (файл voice-*.webm);
//   • распознанный текст — существующая колонка chat_messages.text
//     (именно так уже хранился текст голосовых: text + audio-attachment);
//   • правка — существующий messenger.editMessage (edited_at + edit_history).
//
// `transcript_edited_by_user` НЕ дублируется в БД: признак — серверный
// edited_at (если текст правил пользователь, STT его не перезаписывает —
// клиент вообще не пишет текст после отправки). Функция ниже — единая
// проверка признака для UI и тестов.
// ============================================================================

export interface TranscriptAttachment {
  name: string;
  fileType?: string;
}

export interface TranscriptMessageLike {
  text?: string;
  editedAt?: string;
  attachments?: TranscriptAttachment[];
}

/** Голосовое ли вложение (имя voice-… / MIME audio/webm|opus). */
export function isVoiceAttachment(a: TranscriptAttachment): boolean {
  const mime = String(a.fileType || '').toLowerCase().split(';')[0].trim();
  if (mime === 'audio/webm' || mime === 'audio/opus' || mime === 'audio/x-opus+ogg') return true;
  if (mime && mime !== 'application/octet-stream') return false;
  const name = String(a.name || '').toLowerCase().split('/').pop() || '';
  return name.startsWith('voice-') || name.startsWith('голосовое-') || name.endsWith('.webm');
}

/** Есть ли у сообщения голосовое вложение И текст расшифровки. */
export function isVoiceTranscriptMessage(m: TranscriptMessageLike): boolean {
  const text = String(m.text || '').trim();
  if (!text) return false;
  return (m.attachments || []).some(isVoiceAttachment);
}

/** Текст-расшифровка (trim); пусто — сообщение только с аудио. */
export function transcriptText(m: TranscriptMessageLike): string {
  return isVoiceTranscriptMessage(m) ? String(m.text || '').trim() : '';
}

/**
 * Пользователь правил расшифровку вручную (серверный edited_at).
 * STT не перезаписывает такой текст — флаг вычисляемый, не хранится.
 */
export function transcriptEditedByUser(m: TranscriptMessageLike): boolean {
  return isVoiceTranscriptMessage(m) && !!m.editedAt;
}

/** Нужна ли кнопка «показать полностью» (больше 2 строк по объёму). */
export function transcriptNeedsToggle(text: string, maxChars = 90): boolean {
  const value = String(text || '');
  if (!value) return false;
  return value.length > maxChars || value.split('\n').length > 2;
}
